import React from 'react';
import { BriefcaseIcon, CarIcon, BuildingIcon, HeartPulseIcon, HelpCircleIcon, PhoneIcon, ShieldCheckIcon, ClipboardListIcon, FileSearchIcon, MessagesSquareIcon, ScaleIcon, MapPinIcon, StarIcon, ClockIcon, UsersIcon, CheckCircle2Icon, HeartHandshakeIcon, BoxIcon } from 'lucide-react';
// Firm-wide constants — single source of truth, kept literal for Bricks portability.
// Verified from separoviclawyers.com, the ABR (ABN registry) and LinkedIn.
export const PHONE_DISPLAY = '08 9227 1000';
export const PHONE_TEL = 'tel:+61892271000';
export const EMAIL = '[email protected]';
export const EMAIL_HREF = 'mailto:[email protected]';
export const ADDRESS = '453 Beaufort Street, Highgate WA 6003';
export const HOURS = 'Mon–Fri, 8:30am–5pm';
export const ABN = '63 463 286 531';
export const EST_YEAR = 1990;
export const HIGHGATE_SINCE = 1993;
export const LEGAL_NAME = 'Separovic Injury Lawyers';
export const LOGO_URL = 'https://separoviclawyers.com/wp-content/uploads/2018/07/Separovic-Lawyers-Logo-Black.png';
// Lucide outline iconMap — import only what is used across sections
export const iconMap: Record<string, BoxIcon> = {
  work: BriefcaseIcon,
  road: CarIcon,
  public: BuildingIcon,
  serious: HeartPulseIcon,
  family: HeartHandshakeIcon,
  unsure: HelpCircleIcon,
  phone: PhoneIcon,
  shield: ShieldCheckIcon,
  clipboard: ClipboardListIcon,
  review: FileSearchIcon,
  explain: MessagesSquareIcon,
  scale: ScaleIcon,
  location: MapPinIcon,
  star: StarIcon,
  clock: ClockIcon,
  team: UsersIcon,
  check: CheckCircle2Icon
};
interface SectionProps {
  id?: string;
  children: React.ReactNode;
  className?: string;
  as?: 'section' | 'div';
}
// Consistent responsive section padding: mobile 48–64 / tablet 72–88 / desktop 96–120
export function Section({
  id,
  children,
  className = '',
  as = 'section'
}: SectionProps) {
  const Tag = as;
  return <Tag id={id} className={`w-full py-14 md:py-20 lg:py-28 ${className}`} data-id="element-354">
      <div className="mx-auto w-full max-w-6xl px-5 sm:px-6 lg:px-8" data-id="element-355">
        {children}
      </div>
    </Tag>;
}
// The signature "guidance line" motif — CSS-only decorative rule
export function GuidanceLine({
  className = ''
}: {
  className?: string;
}) {
  return <span aria-hidden="true" className={`guidance-line ${className}`} data-id="element-356" />;
}
interface CTAButtonProps {
  href: string;
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost';
  className?: string;
  ariaLabel?: string;
}
export function CTAButton({
  href,
  children,
  variant = 'primary',
  className = '',
  ariaLabel
}: CTAButtonProps) {
  const base = 'inline-flex items-center justify-center gap-2 min-h-[50px] px-6 rounded-btn text-body font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal focus-visible:ring-offset-2';
  const variants = {
    // Primary = conversion accent so CTAs pop off the page
    primary: 'bg-signal text-paper shadow-[0_6px_20px_-8px_rgba(228,87,46,0.7)] hover:bg-signal-600 hover:-translate-y-0.5',
    // Secondary = quiet outline on light surfaces
    secondary: 'bg-transparent text-forest border border-forest/25 hover:border-forest hover:bg-forest/5',
    // Ghost = solid forest, used on light surfaces where a calm primary is wanted
    ghost: 'bg-forest text-paper hover:bg-forest-700 hover:-translate-y-0.5'
  };
  return <a href={href} aria-label={ariaLabel} className={`${base} ${variants[variant]} ${className}`} data-id="element-357">
      {children}
    </a>;
}
// Small proof/label pill — fully rounded only for small labels per shape system
export function Pill({
  children
}: {
  children: React.ReactNode;
}) {
  return <span className="inline-flex items-center gap-1.5 rounded-full border border-ink/10 bg-paper px-3 py-1 text-small font-medium text-slate" data-id="element-358">
      {children}
    </span>;
}
export function SectionEyebrow({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="flex items-center gap-3" data-id="element-359">
      <GuidanceLine data-id="element-360" />
      <span className="text-small font-semibold uppercase tracking-[0.16em] text-gold" data-id="element-361">
        {children}
      </span>
    </div>;
}
// Standardised section header: eyebrow -> H2 -> optional intro, with one
// consistent vertical rhythm and type scale across every section. `tone`
// swaps colours for use on dark (forest) bands. `align` centres the block.
interface SectionHeadingProps {
  eyebrow: string;
  title: React.ReactNode;
  intro?: React.ReactNode;
  tone?: 'light' | 'dark';
  align?: 'left' | 'center';
  className?: string;
}
export function SectionHeading({
  eyebrow,
  title,
  intro,
  tone = 'light',
  align = 'left',
  className = ''
}: SectionHeadingProps) {
  const isDark = tone === 'dark';
  const centered = align === 'center';
  return <div className={`${centered ? 'mx-auto max-w-2xl text-center' : 'max-w-2xl'} ${className}`} data-id="element-362">
      <div className={centered ? 'flex justify-center' : ''} data-id="element-363">
        <SectionEyebrow data-id="element-364">{eyebrow}</SectionEyebrow>
      </div>
      <h2 className={`mt-5 font-serif text-[32px] font-medium leading-[1.12] tracking-[-0.015em] md:text-h2 ${isDark ? 'text-paper' : 'text-forest'}`} data-id="element-365">
        {title}
      </h2>
      {intro && <p className={`mt-4 text-body-lg ${isDark ? 'text-paper/75' : 'text-slate'}`} data-id="element-366">
          {intro}
        </p>}
    </div>;
}